import { graphql, useStaticQuery } from 'gatsby'
import uniq from 'lodash/uniq'
import React, { useState } from 'react'
import { useReadTime } from '../../hooks/useReadTime'
import { useSiteId } from '../../hooks/useSiteId'
import MagazineThemeHero from '../molecules/MagazinThemeHero'
import MagazinArticles from '../organisms/MagazinArticles'
import MagazinArticlesTags from '../organisms/MagazinArticlesTags'
import MagazinFeatureArticle from '../organisms/MagazinFeatureArticle'
import MagazinHero from '../organisms/MagazinHero'

const articlesPerView = 6

const M19MagazineHomepage = ({ liftStoriesName, anchorLinkId }) => {
  const siteId = useSiteId()
  const data = useStaticQuery(graphql`
    {
      articles: craftContent {
        entries(
          type: "default"
          section: "article"
          siteId: [1, 2, 3]
          orderBy: "postDate DESC"
        ) {
          ... on CraftContent_article_default_Entry {
            id
            title
            siteId
            slug
            postDate
            isFeatured
            referencedThemes {
              ... on CraftContent_theme_default_Entry {
                id
                title
              }
            }
            featureImage {
              ... on CraftContent_images_Asset {
                id
                url
                small_url: url(transform: "small")
              }
            }
            articleBlocks {
              __typename
              ... on CraftContent_articleBlocks_magazineHeader_BlockType {
                id
                variant
                authorPhotographer
                headline
                lead
                image {
                  url
                }
              }
              ... on CraftContent_articleBlocks_magazineCopy_BlockType {
                id
                copyContent
              }
              ... on CraftContent_articleBlocks_magazineMediaContainer_BlockType {
                id
                images {
                  ... on CraftContent_images_BlockType {
                    id
                    image {
                      url
                    }
                    youtubeId
                    caption
                  }
                }
              }
              ... on CraftContent_articleBlocks_magazineStatement_BlockType {
                id
                text
                author
              }
              ... on CraftContent_articleBlocks_flexiblePhotoGallery_BlockType {
                ...flexiblePhotoGalleryBlockType
              }
            }
          }
        }
      }
    }
  `)
  const [limit, setLimit] = useState(articlesPerView)
  const params =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : null
  const themeParam = params?.get('theme')
  const [theme, setTheme] = useState(themeParam || null)
  const siteArticles = data.articles.entries.filter(
    node => node.siteId === siteId,
  )
  const filteredArticles = siteArticles.filter(
    article => !theme || article.referencedThemes.some(t => t.title === theme),
  )
  const featuredArticles = siteArticles.filter(article => article.isFeatured)
  const standardArticles = theme
    ? filteredArticles
    : siteArticles.filter(article => !article.isFeatured)

  const themes = uniq(
    siteArticles.flatMap(article =>
      article.referencedThemes?.map(({ title }) => title),
    ),
  )

  const loadMore = () => {
    setLimit(limit + articlesPerView)
  }

  const onThemeSelected = themeName => {
    setTheme(themeName)
    setLimit(articlesPerView)

    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const readTimeFeaturedArticle0Minutes = useReadTime(featuredArticles[0])
  const readTimeFeaturedArticle1Minutes = useReadTime(featuredArticles[1])

  return (
    <div id={anchorLinkId}>
      {theme ? (
        <MagazineThemeHero
          name={liftStoriesName}
          theme={theme}
          onResetTheme={() => onThemeSelected(null)}
        />
      ) : (
        <>
          <MagazinHero
            name={liftStoriesName}
            title={featuredArticles[0]?.title}
            slug={featuredArticles[0]?.slug}
            image={featuredArticles[0]?.featureImage[0]}
            lead={featuredArticles[0]?.articleBlocks[0]?.lead}
            authorPhotographer={
              featuredArticles[0]?.articleBlocks[0]?.authorPhotographer
            }
            readTime={readTimeFeaturedArticle0Minutes}
            postDate={featuredArticles[0]?.postDate}
            themes={featuredArticles[0]?.referencedThemes}
            onThemeSelected={onThemeSelected}
          />
          <MagazinFeatureArticle
            title={featuredArticles[1]?.title}
            slug={featuredArticles[1]?.slug}
            image={featuredArticles[1]?.featureImage[0]}
            postDate={featuredArticles[1]?.postDate}
            lead={featuredArticles[1]?.articleBlocks[0]?.lead}
            authorPhotographer={
              featuredArticles[1]?.articleBlocks[0]?.authorPhotographer
            }
            readTime={readTimeFeaturedArticle1Minutes}
            themes={featuredArticles[1]?.referencedThemes}
            onThemeSelected={onThemeSelected}
          />
        </>
      )}
      <MagazinArticles articles={standardArticles.slice(0, limit)} />
      <MagazinArticlesTags
        themes={themes}
        selectedTheme={theme}
        onLoadMore={loadMore}
        onThemeSelected={onThemeSelected}
        showLoadMore={limit < standardArticles.length}
      />
    </div>
  )
}

export default M19MagazineHomepage
